/**
 * Platform facts that decide which keyboard shortcuts to listen for and how to
 * label them. Apple platforms use ⌘ for app shortcuts, and Ctrl there is a
 * text-editing modifier (Ctrl+F moves the cursor forward), so a shortcut must
 * bind the right key per platform — not both.
 *
 * Read at call time, not at import: tests stub `navigator`, and SSR-less code
 * paths still guard for its absence.
 */

interface INavigatorWithUserAgentData extends Navigator {
    readonly userAgentData?: { readonly platform?: string };
}

/** macOS, iOS and iPadOS — where ⌘ is the app-shortcut modifier. */
export function isApplePlatform(): boolean {
    if (typeof navigator === 'undefined') return false;
    const nav = navigator as INavigatorWithUserAgentData;
    const platform = nav.userAgentData?.platform ?? nav.platform ?? '';
    return /mac|iphone|ipad|ipod/i.test(platform);
}

/** Whether a keydown holds the platform's app-shortcut modifier and not the other one. */
export function hasPlatformModifier(event: KeyboardEvent): boolean {
    return isApplePlatform() ? event.metaKey && !event.ctrlKey : event.ctrlKey && !event.metaKey;
}

/** How to show a Mod+key shortcut: `⌘F` on Apple platforms, `Ctrl+F` elsewhere. */
export function modKeyLabel(key: string): string {
    return isApplePlatform() ? `⌘${key.toUpperCase()}` : `Ctrl+${key.toUpperCase()}`;
}

/** The `aria-keyshortcuts` value for a Mod+key shortcut. */
export function modKeyAria(key: string): string {
    return isApplePlatform() ? `Meta+${key.toUpperCase()}` : `Control+${key.toUpperCase()}`;
}
